import { getShipmentStatusTimestampKey } from '@frontend/lib/booking'
import { NotificationType } from '@frontend-types/domain'

import { TimestampValue } from '@shared-types/shipments'

export enum ContactType {
  SUPPLIER = 'supplier',
  TRANSPORTER = 'transporter',
  COMPANY = 'company',
}

export enum OrderType {
  PURCHASE = 'PURCHASE',
}

export const NOTIFICATION_STATUS = {
  success: 'success' as NotificationType,
  info: 'info' as NotificationType,
  warning: 'warning' as NotificationType,
  error: 'error' as NotificationType,
}

export const TIME_STAMP_FIELDS = {
  CHECKED_IN: {
    label: 'Checked in',
    key: 'checked_in_at',
    dateKey: 'checked_in_at__date',
    notesKey: 'checked_in_notes',
  },
  TRUCK_ARRIVED_AT: {
    label: 'At ramp',
    key: 'truck_arrived_at',
    dateKey: 'truck_arrived_at__date',
    notesKey: 'truck_arrived_notes',
  },
  UNLOAD_COMPLETE_AT: {
    label: 'Delivered',
    key: 'unload_completed_at',
    dateKey: 'unload_completed_at__date',
    notesKey: 'unload_completed_notes',
  },
  PARTIAL_UNLOAD_COMPLETE_AT: {
    label: 'Partially received',
    key: 'unload_completed_at',
    dateKey: 'unload_completed_at__date',
    notesKey: 'unload_completed_notes',
  },
  REJECTED_AT: {
    label: 'Rejected',
    key: 'rejected_at',
    dateKey: 'rejected_at__date',
    notesKey: 'rejected_notes',
  },
  EXITED_AT: {
    label: 'Exited',
    key: 'exited_at',
    dateKey: 'exited_at__date',
    notesKey: 'exited_notes',
  },
  CANCELLED_AT: {
    label: 'Cancelled',
    key: 'cancelled_at',
    dateKey: 'cancelled_at__date',
    notesKey: 'cancelled_notes',
  },
} as const

export const getTimestampFields = (includeOnly?: ShipmentStatus[]) => {
  const filterOutPartialUnloadCompleteAt = (field: TimestampValue) =>
    field.label !== TIME_STAMP_FIELDS.PARTIAL_UNLOAD_COMPLETE_AT.label

  if (includeOnly && includeOnly.length > 0) {
    return includeOnly
      .map((status) => getShipmentStatusTimestampKey(status))
      .filter((timestamp): timestamp is TimestampValue => !!timestamp)
  }
  return Object.values(TIME_STAMP_FIELDS).filter(filterOutPartialUnloadCompleteAt)
}

export const SHIPPING_UNIT_TYPES = ['PALLETISED', 'NON_PALLETISED', 'CONTAINERS', 'BAGS'] as const

export const VOUCHER_UNIT_TYPES = ['EPAL_80X120', 'DISPOSABLE_PALLET', 'DUSSELDORF'] as const

export const VEHICLE_TYPES = [
  'Small truck (up to 3.5t)',
  'Truck rear loading (up to 10t)',
  'Truck rear loading (up to 40t)',
  'Road train (up to 45t)',
  'Truck container (20ft)',
  'Truck container (40ft)',
  'Truck curtain sider (up to 40t)',
  'Refrigerated car',
  'Standard van',
  'Type unknown',
] as const

export enum ShipmentStatus {
  CREATED = 'CREATED',
  CHECKED_IN = 'CHECKED_IN',
  RAMP_ASSIGNED = 'RAMP_ASSIGNED',
  ENTERED_FACILITY = 'ENTERED_FACILITY',
  AT_RAMP = 'AT_RAMP',
  UNLOADED = 'UNLOADED',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
  DELIVERED = 'DELIVERED',
  EXITED = 'EXITED',
  PLACEHOLDER = 'PLACEHOLDER',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  QUARANTINE = 'QUARANTINE',
}

// Transportation statuses mirror a subset of ShipmentStatus values
export enum TransportationStatus {
  CHECKED_IN = 'CHECKED_IN',
  RAMP_ASSIGNED = 'RAMP_ASSIGNED',
  ENTERED_FACILITY = 'ENTERED_FACILITY',
  DELIVERED = 'DELIVERED',
  EXITED = 'EXITED',
}

export enum ShipmentPurchaseOrderStatus {
  CREATED = 'CREATED',
  CHECKED_IN = 'CHECKED_IN',
  AT_RAMP = 'AT_RAMP',
  IN_STAGING = 'IN_STAGING',
  PROCESSING = 'PROCESSING',
  PUT_AWAY = 'PUT_AWAY',
  CHECKS_IN_PROGRESS = 'CHECKS_IN_PROGRESS',
  IN_QUARANTINE = 'IN_QUARANTINE',
  COMPLETE = 'COMPLETE',
  REJECTED = 'REJECTED',
}

export enum PurchaseOrderStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

export enum ShipmentState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  DRAFT = 'DRAFT',
}

export enum SupportedUnitLimits {
  PALLETISED = 'PALLETISED',
  CONTAINERS = 'CONTAINERS',
}

export const LIMIT_KINDS = ['UNIT_TYPE', 'TRUCK_TYPE'] as const

// Truck types whose TRUCK_TYPE limits share a single aggregate pool: e.g. a department may allow
// "3 40ft OR 2 40ft + 1 20ft" per day rather than each limit being independent.
export const TRUCK_TYPE_LIMIT_GROUPS: readonly (readonly string[])[] = [
  ['Truck container (20ft)', 'Truck container (40ft)'],
] as const

export enum InboundOutboundType {
  OUTBOUND = 'OUTBOUND',
  INBOUND = 'INBOUND',
}

export const InboundOutboundTypeValues = Object.values(InboundOutboundType) as [string, ...string[]]

export enum SlotState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ChecklistState {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum ChecklistStatus {
  CREATED = 'CREATED',
  INVOICED = 'INVOICED',
  NON_INVOICEABLE = 'NON_INVOICEABLE',
}

export enum BookingApprovalStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum ViewContext {
  SUPPLIER = 'supplier',
  OWNER = 'owner',
  TRANSPORTER = 'transporter',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  READONLY = 'READONLY',
}

export enum UserLocale {
  ENGLISH = 'en-GB',
  ITALIAN = 'it-IT',
}

export enum InvitationStatus {
  CREATED = 'CREATED',
  CLAIMED = 'CLAIMED',
  REVOKED = 'REVOKED',
  EXPIRED = 'EXPIRED',
}

export enum CompanyStatus {
  UNCLAIMED = 'UNCLAIMED',
  CLAIMED = 'CLAIMED',
}

export enum TransporeonAction {
  BOOKED = 'BOOKED',
  DELETED = 'DELETED',
}

export enum UpdateActionResult {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export enum ChecklistExportTypes {
  SERVICE_LEVEL = 'SERVICE_LEVEL',
  CHECKLIST_LEVEL = 'CHECKLIST_LEVEL',
  PO_LEVEL = 'PO_LEVEL',
}

export enum ChecklistExportPermissionTypes {
  FULL = 'FULL',
  PARTIAL = 'PARTIAL',
}

export enum FormLocation {
  CHECK_IN_OUT_FLOW = 'CHECK_IN_OUT_FLOW',
}

export enum VisitStatus {
  ENTERED = 'ENTERED',
  ON_BREAK = 'ON_BREAK',
  EXITED = 'EXITED',
}

export enum YardAlertTypes {
  CHECK_IN_DELAY = 'CHECK_IN_DELAY',
  CHECK_IN_PRIORITY = 'CHECK_IN_PRIORITY',
  BOOKING_DELAY = 'BOOKING_DELAY',
  BOOKING_PRIORITY = 'BOOKING_PRIORITY',
}

export enum DriverDocumentTypes {
  DRIVER_LICENSE = 'DRIVER_LICENSE',
  ID_CARD = 'ID_CARD',
  PASSPORT = 'PASSPORT',
}

export const ShipmentSummariesSortByValues = [
  'createdAt',
  '-createdAt',
  'shipment_status',
  '-shipment_status',
  'booking__start_time',
  '-booking__start_time',
  'processing__checked_in_at',
  '-processing__checked_in_at',
] as const

export const PurchaseOrderSortByValues = [
  'due_date',
  '-due_date',
  'is_urgent',
  '-is_urgent',
] as const

export const ChecklistDeviceSortByValues = ['last_online_at', '-last_online_at'] as const

export const ShipmentPurchaseOrderSortByValues = [
  'is_urgent',
  '-is_urgent',
  'unload_completed_at',
  '-unload_completed_at',
] as const

export const ChecklistSortByValues = [
  'locally_updated_at',
  '-locally_updated_at',
  'delivery_date',
  '-delivery_date',
] as const

export const ListCompanyAuditEventsSortByValues = ['createdAt', '-createdAt'] as const

export enum Capacity {
  NONE = 'NONE',
  AVAILABLE = 'AVAILABLE',
}

export const MILISECONDS_IN_A_DAY = 24 * 3600000

export enum VoucherStatus {
  ACTIVE = 'ACTIVE',
  REDEEMED = 'REDEEMED',
}

export enum VoucherState {
  UNLOCKED = 'UNLOCKED',
  LOCKED = 'LOCKED',
  BOOKED = 'BOOKED',
}

export enum VoucherInvalidReasonCode {
  VOUCHER_LOCKED = 'VOUCHER_LOCKED',
  VOUCHER_REDEEMED = 'VOUCHER_REDEEMED',
  VOUCHER_NOT_FOUND = 'VOUCHER_NOT_FOUND',
  VOUCHER_UNKNOWN = 'VOUCHER_UNKNOWN',
  VOUCHER_WRONG_FACILITY = 'VOUCHER_WRONG_FACILITY',
}

export enum ChecklistInvalidReasonCode {
  CHECKLIST_NOT_REDEEMABLE = 'CHECKLIST_NOT_REDEEMABLE',
  CHECKLIST_NOT_FOUND = 'CHECKLIST_NOT_FOUND',
  CHECKLIST_UNKNOWN = 'CHECKLIST_UNKNOWN',
}

export enum COMPANY_MANAGEMENT_PERMISSIONS {
  VIEW_MANAGEMENT = 'VIEW_MANAGEMENT',
  MANAGE_PERMISSIONS = 'MANAGE_PERMISSIONS',
  MANAGE_USERS = 'MANAGE_USERS',
  MANAGE_DEPARTMENTS = 'MANAGE_DEPARTMENTS',
  MANAGE_CONTACTS = 'MANAGE_CONTACTS',
  MANAGE_FACILITIES = 'MANAGE_FACILITIES',
}

export enum COMPANY_SHIPMENT_PERMISSIONS {
  VIEW_SHIPMENTS = 'VIEW_SHIPMENTS',
  CREATE_SHIPMENTS = 'CREATE_SHIPMENTS',
  UPDATE_SHIPMENT_DETAILS = 'UPDATE_SHIPMENT_DETAILS',
  UPDATE_SHIPMENT_STATUS = 'UPDATE_SHIPMENT_STATUS',
  VIEW_EXITED_SHIPMENTS = 'VIEW_EXITED_SHIPMENTS',
}

export enum COMPANY_BOOKING_PERMISSIONS {
  VIEW_BOOKINGS = 'VIEW_BOOKINGS',
  CREATE_SLOTS = 'CREATE_SLOTS',
  CANCEL_BOOKINGS = 'CANCEL_BOOKINGS',
  MANAGE_BOOKING_LIMITS = 'MANAGE_BOOKING_LIMITS',
}

export enum COMPANY_YARD_MANAGEMENT_PERMISSIONS {
  VIEW_YARD_MANAGEMENT = 'VIEW_YARD_MANAGEMENT',
  CHECK_IN_SHIPMENTS = 'CHECK_IN_SHIPMENTS',
  MODIFY_ANOMALIES = 'MODIFY_ANOMALIES',
  SHOW_TRANSPORTATION_TAB = 'SHOW_TRANSPORTATION_TAB',
}

export enum COMPANY_PALLET_MANAGEMENT_PERMISSIONS {
  VIEW_PALLET_MANAGEMENT = 'VIEW_PALLET_MANAGEMENT',
  CREATE_VOUCHERS = 'CREATE_VOUCHERS',
  EDIT_VOUCHERS = 'EDIT_VOUCHERS',
  REDEEM_VOUCHERS = 'REDEEM_VOUCHERS',
  UNLOCK_VOUCHERS = 'UNLOCK_VOUCHERS',
}

export enum COMPANY_VISITOR_PERMISSIONS {
  VIEW_VISITORS = 'VIEW_VISITORS',
  CREATE_VISITS = 'CREATE_VISITS',
}

export enum COMPANY_CHECKLIST_PERMISSIONS {
  VIEW_CHECKLISTS = 'VIEW_CHECKLISTS',
  MANAGE_CHECKLIST_SERVICES = 'MANAGE_CHECKLIST_SERVICES',
}

export enum COMPANY_ANALYTICS_PERMISSIONS {
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
  FACILITY_ANALYTICS = 'FACILITY_ANALYTICS',
  DEPARTMENT_ANALYTICS = 'DEPARTMENT_ANALYTICS',
}

export type AnyCompanyPermissionType =
  | COMPANY_MANAGEMENT_PERMISSIONS
  | COMPANY_SHIPMENT_PERMISSIONS
  | COMPANY_BOOKING_PERMISSIONS
  | COMPANY_YARD_MANAGEMENT_PERMISSIONS
  | COMPANY_PALLET_MANAGEMENT_PERMISSIONS
  | COMPANY_VISITOR_PERMISSIONS
  | COMPANY_CHECKLIST_PERMISSIONS
  | COMPANY_ANALYTICS_PERMISSIONS

export const COMPANY_AVAILABLE_PERMISSIONS = [
  ...Object.values(COMPANY_MANAGEMENT_PERMISSIONS),
  ...Object.values(COMPANY_SHIPMENT_PERMISSIONS),
  ...Object.values(COMPANY_BOOKING_PERMISSIONS),
  ...Object.values(COMPANY_YARD_MANAGEMENT_PERMISSIONS),
  ...Object.values(COMPANY_PALLET_MANAGEMENT_PERMISSIONS),
  ...Object.values(COMPANY_VISITOR_PERMISSIONS),
  ...Object.values(COMPANY_CHECKLIST_PERMISSIONS),
  ...Object.values(COMPANY_ANALYTICS_PERMISSIONS),
]

export enum FACILITY_GENERAL_PERMISSIONS {
  FACILITY_ACCESS = 'FACILITY_ACCESS',
}

export enum FACILITY_SHIPMENT_PERMISSIONS {
  VIEW_SHIPMENTS = 'VIEW_SHIPMENTS',
  CREATE_SHIPMENTS = 'CREATE_SHIPMENTS',
  UPDATE_SHIPMENT_DETAILS = 'UPDATE_SHIPMENT_DETAILS',
  UPDATE_SHIPMENT_STATUS = 'UPDATE_SHIPMENT_STATUS',
  VIEW_EXITED_SHIPMENTS = 'VIEW_EXITED_SHIPMENTS',
}

export enum FACILITY_BOOKING_PERMISSIONS {
  VIEW_BOOKINGS = 'VIEW_BOOKINGS',
  CREATE_SLOTS = 'CREATE_SLOTS',
  CANCEL_BOOKINGS = 'CANCEL_BOOKINGS',
  MANAGE_BOOKING_LIMITS = 'MANAGE_BOOKING_LIMITS',
}

export enum FACILITY_YARD_MANAGEMENT_PERMISSIONS {
  VIEW_YARD_MANAGEMENT = 'VIEW_YARD_MANAGEMENT',
  CHECK_IN_SHIPMENTS = 'CHECK_IN_SHIPMENTS',
  MODIFY_ANOMALIES = 'MODIFY_ANOMALIES',
  SHOW_TRANSPORTATION_TAB = 'SHOW_TRANSPORTATION_TAB',
}

export enum FACILITY_PALLET_MANAGEMENT_PERMISSIONS {
  VIEW_PALLET_MANAGEMENT = 'VIEW_PALLET_MANAGEMENT',
  CREATE_VOUCHERS = 'CREATE_VOUCHERS',
  EDIT_VOUCHERS = 'EDIT_VOUCHERS',
  REDEEM_VOUCHERS = 'REDEEM_VOUCHERS',
  UNLOCK_VOUCHERS = 'UNLOCK_VOUCHERS',
}

export enum FACILITY_VISITOR_PERMISSIONS {
  VIEW_VISITORS = 'VIEW_VISITORS',
  CREATE_VISITS = 'CREATE_VISITS',
}

export enum FACILITY_CHECKLIST_PERMISSIONS {
  VIEW_CHECKLISTS = 'VIEW_CHECKLISTS',
  MANAGE_CHECKLIST_SERVICES = 'MANAGE_CHECKLIST_SERVICES',
}

export enum FACILITY_ANALYTICS_PERMISSIONS {
  VIEW_ANALYTICS = 'VIEW_ANALYTICS',
  FACILITY_ANALYTICS = 'FACILITY_ANALYTICS',
  DEPARTMENT_ANALYTICS = 'DEPARTMENT_ANALYTICS',
}

export type AnyFacilityPermissionType =
  | FACILITY_GENERAL_PERMISSIONS
  | FACILITY_SHIPMENT_PERMISSIONS
  | FACILITY_BOOKING_PERMISSIONS
  | FACILITY_YARD_MANAGEMENT_PERMISSIONS
  | FACILITY_PALLET_MANAGEMENT_PERMISSIONS
  | FACILITY_VISITOR_PERMISSIONS
  | FACILITY_CHECKLIST_PERMISSIONS
  | FACILITY_ANALYTICS_PERMISSIONS

export const FACILITY_AVAILABLE_PERMISSIONS = [
  ...Object.values(FACILITY_GENERAL_PERMISSIONS),
  ...Object.values(FACILITY_SHIPMENT_PERMISSIONS),
  ...Object.values(FACILITY_BOOKING_PERMISSIONS),
  ...Object.values(FACILITY_YARD_MANAGEMENT_PERMISSIONS),
  ...Object.values(FACILITY_PALLET_MANAGEMENT_PERMISSIONS),
  ...Object.values(FACILITY_VISITOR_PERMISSIONS),
  ...Object.values(FACILITY_CHECKLIST_PERMISSIONS),
  ...Object.values(FACILITY_ANALYTICS_PERMISSIONS),
]

export enum SourceType {
  INTEGRATION = 'INTEGRATION',
  PLATFORM = 'PLATFORM',
}

export const YardStatusesFilters = [
  ShipmentStatus.CHECKED_IN,
  ShipmentStatus.RAMP_ASSIGNED,
  ShipmentStatus.ENTERED_FACILITY,
  ShipmentStatus.AT_RAMP,
  ShipmentStatus.UNLOADED,
  ShipmentStatus.DELIVERED,
  ShipmentStatus.CANCELLED,
  ShipmentStatus.REJECTED,
]

export enum ShipmentAnomalies {
  SMS_NOTIFICATION_FAILURE = 'SMS_NOTIFICATION_FAILURE',
  COUNTERPART_BLACKLISTED = 'COUNTERPART_BLACKLISTED',
  TRANSPORTER_BLACKLISTED = 'TRANSPORTER_BLACKLISTED',
}

// Mirrors the slot cutoff schedule hardcoded in scheduled_worker/src/utils/timezone.ts — keep in sync
export const SLOT_CUTOFF_TIME_ZONE = 'Europe/Rome'
export const SLOT_CUTOFF_HOUR = 17
export const SLOT_CUTOFF_MINUTE = 30

export enum VigilateEventDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

export enum VigilateEventType {
  GATE_ENTRY = 'GATE_ENTRY',
  GATE_EXIT = 'GATE_EXIT',
}

export enum VigilateEventStatus {
  PROCESSING = 'PROCESSING',
  SUCCESS = 'SUCCESS',
  UNMATCHED = 'UNMATCHED',
  FAILED = 'FAILED',
  DUPLICATE = 'DUPLICATE',
}

export enum VigilateGateEventType {
  ENTRY = 'ENTRY',
  EXIT = 'EXIT',
}
